import { Form, Formik, type FormikConfig, type FormikValues } from "formik";
import type React from "react";
import { ErrorBanner } from "../Error/ErrorBanner";
import { Button } from "../Theme/button";

export interface EntityFormProps<TValues extends FormikValues>
  extends Pick<
    FormikConfig<TValues>,
    | "initialValues"
    | "onSubmit"
    | "validationSchema"
    | "enableReinitialize"
    | "validateOnMount"
  > {
  /** Data-source / submit error to surface under the form. */
  error?: Error;
  submitLabel?: string;
  children: React.ReactNode;
}

/**
 * The shared shell for create/update forms: a Formik form rendering the supplied
 * fields, a submit button disabled while submitting or invalid, and an
 * {@link ErrorBanner}. Pair with {@link useEntityForm} for the submit handler.
 */
export function EntityForm<TValues extends FormikValues>({
  error,
  submitLabel = "Save",
  children,
  ...formik
}: EntityFormProps<TValues>): React.ReactElement {
  return (
    <Formik {...formik}>
      {({ isValid, isSubmitting }): React.ReactElement => (
        <>
          <Form>
            {children}
            <Button
              type="submit"
              color="brandLight"
              disabled={isSubmitting || !isValid}
            >
              {submitLabel}
            </Button>
          </Form>
          <ErrorBanner error={error} />
        </>
      )}
    </Formik>
  );
}
